import { get } from 'lodash-es';
import { getChannelsOptions, getCountries, getSortedLangs, getKeywordsOptions } from 'pages/upload.page/utils';
import React from 'react';
import { Field } from 'redux-form';
import { FIELDS } from './form-fields/enum';
import { Input, SelectDecorated, TextArea, TextBox } from './index';
import { getI18nLng } from 'utils/i18n';

/**
 * Rule to follow - each method should receive first 2 params (config, key, ...)
 * All other params passed to method are optional and should be handled from case to case.
 */
export const CustomFieldsMap = {
  /**
   * @param {string} displayName
   * @param {boolean} required
   * @param {string|number} key
   * @return {*}
   */

  [FIELDS.VIDEO_TITLE]: ({ displayName, required }, key) => (
    <Field
      name={FIELDS.VIDEO_TITLE}
      component={Input}
      type="text"
      label={`${displayName} ${required ? '*' : ''}`}
      key={key}
    />
  ),

  /**
   * @param {string} displayName
   * @param {boolean} required
   * @param {string|number} key
   * @return {*}
   */
  [FIELDS.DESCRIPTION]: ({ displayName, required }, key) => (
    <Field
      name="description"
      hashtagSupport="true"
      component={TextArea}
      label={`${displayName} ${required ? '*' : ''}`}
      key={key}
    />
  ),

  /**
   * @param {string} displayName
   * @param {boolean} required
   * @param {number} key
   * @return {*}
   */
  [FIELDS.TEXT_BOX]: ({ displayName, required, data }, key) => {
    return (
      <Field
        name={FIELDS.TEXT_BOX}
        component={TextBox}
        key={key}
        props={{
          placeholder: displayName || 'Empty text box',
          text: data,
          required,
        }}
      />
    );
  },

  /**
   * Fields based on MultiSelect Plugin
   */

  /**
   * @param {string} displayName
   * @param {boolean} required
   * @param {string|number} key
   * @param {{id: number, parentId: number, name: string}[]} channels
   * @param {string} formName
   * @param {{id: number}[]} defaultValue - list of selected channels
   * @return {*}
   */
  [FIELDS.CHANNEL_SELECT]: ({ displayName, required, data }, key, name, formName, formValues) => (
    <Field
      name={`${name}_${key}`}
      component={SelectDecorated}
      label={displayName}
      key={key}
      props={{
        options: getChannelsOptions(data),
        isMulti: true,
        closeMenuOnSelect: false,
        required,
        name: `${name}_${key}`,
        formName,
        selectedValues: get(formValues, `${name}_${key}`, []),
      }}
    />
  ),

  /**
   * @param {string} displayName
   * @param {string|number} key
   * @param {boolean} required
   * @return {*}
   */
  [FIELDS.KEYWORDS]: ({ displayName, required, data }, key, name, formName, formValues) => (
    <Field
      name={FIELDS.KEYWORDS}
      hashtagSupport="true"
      component={SelectDecorated}
      label={displayName}
      key={key}
      props={{
        options: getKeywordsOptions(data),
        isMulti: true,
        optimizeSearch: true,
        creatableOptions: true,
        closeMenuOnSelect: false,
        required,
        name,
        formName,
        selectedValues: getKeywordsOptions(get(formValues, FIELDS.KEYWORDS, [])),
      }}
    />
  ),

  /**
   * @param {string[]} value
   * @param {string} displayName
   * @param {boolean} required
   * @param {string} key
   * @returns {*}
   */
  [FIELDS.LANGUAGE_SELECT]: ({ displayName, required }, key, name, formName, formValues) => {
    const options = getSortedLangs().map((language) => ({
      label: language.native,
      value: language.iso,
      id: language.iso,
    }));

    return (
      <Field
        name={name}
        component={SelectDecorated}
        label={displayName}
        key={key}
        props={{
          isMulti: false,
          closeMenuOnSelect: true,
          options,
          placeholder: 'please_select',
          name,
          required,
          formName,
          selectedValues: get(formValues, FIELDS.LANGUAGE_SELECT, []),
        }}
      />
    );
  },

  /**
   * @param {string[]} value
   * @param {string} displayName
   * @param {boolean} required
   * @param {string} key
   * @returns {*}
   */
  [FIELDS.COUNTRY_SELECT]: ({ displayName, required }, key, name, formName, formValues) => {
    const options = getCountries(getI18nLng()).map((county) => ({
      label: county.native,
      value: county.iso,
      id: county.iso,
    }));

    return (
      <Field
        name={name}
        component={SelectDecorated}
        label={displayName}
        key={key}
        props={{
          isMulti: false,
          closeMenuOnSelect: true,
          options,
          placeholder: 'please_select',
          name,
          formName,
          required,
          selectedValues: get(formValues, FIELDS.COUNTRY_SELECT, []),
        }}
      />
    );
  },
};
